import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CreateDestinationFormValues } from '../../organisms/create-destination/create-destination-form';

import CautionCard from '@/components/atoms/caution-card';
import { InputField } from '@/components/atoms/input-field';
import StepTitle from '@/components/atoms/step-title';
import {
  CreateDestinationContext,
  CreateDestinationEvent,
} from '@/features/destinations/utils/machine/create-destination-machine';
import { useOrganizationKiosks } from '@/features/shared/hooks/query/use-organization-kiosks';
import { getMessage } from '@/utils/get-message';

interface Props {
  context: CreateDestinationContext;
  send: (event: CreateDestinationEvent) => void;
}

export const Kiosk = ({ context, send }: Props) => {
  const { t } = useTranslation(['createDestination', 'destination', 'common']);
  const { data: organizationKiosks } = useOrganizationKiosks();

  const { control, setValue } = useFormContext<CreateDestinationFormValues>();

  const [
    allowedKiosks,
    hosts,
    devices,
    selectedDefaultHostId,
    isAddHostStepEnabled,
    isHostObligatory,
    isHostConfirmationObligatory,
    isHostConfirmationInEmail,
    isDeviceConfirmationEnabled,
  ] = useWatch({
    control,
    name: [
      'allowedKiosks',
      'hosts',
      'devices',
      'defaultHostId',
      'addHostStepEnabled',
      'hostObligatory',
      'hostConfirmationObligatory',
      'hostConfirmationInEmail',
      'deviceConfirmation',
    ],
  });
  const isKioskEnabled = (allowedKiosks?.length || 0) > 0;
  const normalizedDefaultHostId = selectedDefaultHostId ?? null;
  const isDefaultHostAssigned = normalizedDefaultHostId !== null;
  const hasHosts = (hosts?.length || 0) > 0;
  const canUseDefaultHost = hasHosts && isKioskEnabled;
  const canUseHostStep = isKioskEnabled && !isDefaultHostAssigned;
  const canUseHostObligatory = isAddHostStepEnabled;
  const canUseHostConfirmation = isDefaultHostAssigned
    ? isKioskEnabled
    : canUseHostStep && isHostObligatory;
  const hasAssignedDevices = (devices?.length || 0) > 0;
  const canUseDeviceConfirmation = isKioskEnabled && hasAssignedDevices;

  useEffect(() => {
    const setValueOptions = {
      shouldDirty: true,
      shouldValidate: true,
    } as const;

    let nextDefaultHostId = normalizedDefaultHostId;
    let nextAddHostStepEnabled = isAddHostStepEnabled;
    let nextHostObligatory = isHostObligatory;
    let nextHostConfirmationObligatory = isHostConfirmationObligatory;
    let nextHostConfirmationInEmail = isHostConfirmationInEmail;
    let nextDeviceConfirmation = isDeviceConfirmationEnabled;

    if (
      nextDefaultHostId !== null &&
      !hosts?.some(host => host.id === nextDefaultHostId)
    ) {
      nextDefaultHostId = null;
    }

    if (!hasHosts) {
      nextDefaultHostId = null;
    }

    if (!isKioskEnabled) {
      nextDefaultHostId = null;
    }

    const hasDefaultHost = nextDefaultHostId !== null;
    const shouldDisableHostFlow = !isKioskEnabled || hasDefaultHost;

    if (shouldDisableHostFlow) {
      nextAddHostStepEnabled = false;
    }

    if (!nextAddHostStepEnabled || shouldDisableHostFlow) {
      nextHostObligatory = false;
    }

    if (!nextHostObligatory && !hasDefaultHost) {
      nextHostConfirmationObligatory = false;
    }

    if (!nextHostConfirmationObligatory) {
      nextHostConfirmationInEmail = false;
    }

    if (!canUseDeviceConfirmation) {
      nextDeviceConfirmation = false;
    }

    if (nextDefaultHostId !== normalizedDefaultHostId) {
      setValue('defaultHostId', nextDefaultHostId, setValueOptions);
    }

    if (nextAddHostStepEnabled !== isAddHostStepEnabled) {
      setValue('addHostStepEnabled', nextAddHostStepEnabled, setValueOptions);
    }

    if (nextHostObligatory !== isHostObligatory) {
      setValue('hostObligatory', nextHostObligatory, setValueOptions);
    }

    if (nextHostConfirmationObligatory !== isHostConfirmationObligatory) {
      setValue(
        'hostConfirmationObligatory',
        nextHostConfirmationObligatory,
        setValueOptions,
      );
    }

    if (nextHostConfirmationInEmail !== isHostConfirmationInEmail) {
      setValue('hostConfirmationInEmail', nextHostConfirmationInEmail, setValueOptions);
    }

    if (nextDeviceConfirmation !== isDeviceConfirmationEnabled) {
      setValue('deviceConfirmation', nextDeviceConfirmation, setValueOptions);
    }
  }, [
    isAddHostStepEnabled,
    isKioskEnabled,
    isHostObligatory,
    isHostConfirmationObligatory,
    isHostConfirmationInEmail,
    isDeviceConfirmationEnabled,
    canUseDeviceConfirmation,
    hasHosts,
    hosts,
    normalizedDefaultHostId,
    setValue,
  ]);

  return (
    <div>
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridAutoFlow: 'row',
        }}>
        <StepTitle
          title={t('common:stepTitle', {
            step: context.step,
            title: t(context.content),
          })}
        />

        <CautionCard
          title={t('common:optional')}
          description={t('createDestination:optionalStepDescription')}
          sx={{ mb: 2 }}
        />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="allowedKiosks"
              render={({ field }) => {
                const selectedKiosks = Array.isArray(field.value)
                  ? field.value
                  : [];
                const selectedKioskIds = selectedKiosks.map(({ id }) => id);

                return (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {t('destination:kiosk.allowedKiosks')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 0.5,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: 'neutral.300',
                        borderRadius: 1,
                        p: 1,
                      }}>
                      {(organizationKiosks || []).map(({ id, name }) => (
                        <FormControlLabel
                          key={id}
                          sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                          labelPlacement="start"
                          control={
                            <Checkbox
                              checked={selectedKioskIds.includes(id)}
                              onChange={(_, checked) => {
                                const nextSelectedKiosks = checked
                                  ? [
                                      ...selectedKiosks.filter(kiosk => kiosk.id !== id),
                                      { id, name },
                                    ]
                                  : selectedKiosks.filter(kiosk => kiosk.id !== id);

                                field.onChange(nextSelectedKiosks);
                              }}
                              onBlur={field.onBlur}
                              name={field.name}
                            />
                          }
                          label={name}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Tooltip
              title={getMessage([
                {
                  check: !isKioskEnabled,
                  message: t('destination:kiosk.addHostStepEnabledDisabled'),
                },
                {
                  check: isKioskEnabled && !hasHosts,
                  message: t('destination:kiosk.defaultHostDisabledNoHosts'),
                },
              ])}
              disableHoverListener={canUseDefaultHost}
              disableFocusListener={canUseDefaultHost}>
              <span>
                <Controller
                  control={control}
                  name="defaultHostId"
                  render={({ field }) => (
                    <InputField
                      fullWidth
                      select
                      value={field.value ?? ''}
                      onChange={event => {
                        const value = event.target.value;
                        const nextDefaultHostId = value === '' ? null : Number(value);

                        field.onChange(nextDefaultHostId);
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      disabled={!canUseDefaultHost}
                      label={t('destination:kiosk.defaultHost')}>
                      <MenuItem value="">{t('destination:kiosk.defaultHostNotSet')}</MenuItem>
                      {(hosts || []).map(host => (
                        <MenuItem key={host.id} value={host.id}>
                          {host.name}
                        </MenuItem>
                      ))}
                    </InputField>
                  )}
                />
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={12}>
            <Tooltip
              title={getMessage([
                {
                  check: !canUseHostStep && !isKioskEnabled,
                  message: t('destination:kiosk.addHostStepEnabledDisabled'),
                },
                {
                  check: !canUseHostStep && isKioskEnabled,
                  message: t('destination:kiosk.addHostStepEnabledDisabledByDefaultHost'),
                },
              ])}
              disableHoverListener={canUseHostStep}
              disableFocusListener={canUseHostStep}>
              <span>
                <Controller
                  control={control}
                  name="addHostStepEnabled"
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                      labelPlacement="start"
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          onBlur={field.onBlur}
                          name={field.name}
                          disabled={!canUseHostStep}
                        />
                      }
                      label={t('destination:kiosk.addHostStepEnabled')}
                    />
                  )}
                />
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={12}>
            <Tooltip
              title={getMessage([
                {
                  check: !canUseHostObligatory && isDefaultHostAssigned,
                  message: t('destination:kiosk.hostObligatoryDisabledByDefaultHost'),
                },
                {
                  check: !canUseHostObligatory && !isDefaultHostAssigned,
                  message: t('destination:kiosk.hostObligatoryDisabled'),
                },
              ])}
              disableHoverListener={canUseHostObligatory}
              disableFocusListener={canUseHostObligatory}>
              <span>
                <Controller
                  control={control}
                  name="hostObligatory"
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                      labelPlacement="start"
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          onBlur={field.onBlur}
                          name={field.name}
                          disabled={!canUseHostObligatory}
                        />
                      }
                      label={t('destination:kiosk.hostObligatory')}
                    />
                  )}
                />
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={12}>
            <Tooltip
              title={getMessage([
                {
                  check: !canUseHostConfirmation && !isKioskEnabled,
                  message: t('destination:kiosk.addHostStepEnabledDisabled'),
                },
                {
                  check: !canUseHostConfirmation && isKioskEnabled,
                  message: t('destination:kiosk.hostConfirmationObligatoryDisabled'),
                },
              ])}
              disableHoverListener={canUseHostConfirmation}
              disableFocusListener={canUseHostConfirmation}>
              <span>
                <Controller
                  control={control}
                  name="hostConfirmationObligatory"
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                      labelPlacement="start"
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          onBlur={field.onBlur}
                          name={field.name}
                          disabled={!canUseHostConfirmation}
                        />
                      }
                      label={t('destination:kiosk.hostConfirmationObligatory')}
                    />
                  )}
                />
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={12}>
            <Tooltip
              title={getMessage([
                {
                  check:
                    !canUseHostConfirmation && !isKioskEnabled,
                  message: t('destination:kiosk.addHostStepEnabledDisabled'),
                },
                {
                  check:
                    !canUseHostConfirmation && isKioskEnabled,
                  message: t('destination:kiosk.hostConfirmationObligatoryDisabled'),
                },
                {
                  check: canUseHostConfirmation && !isHostConfirmationObligatory,
                  message: t('destination:kiosk.hostConfirmationInEmailDisabled'),
                },
              ])}
              disableHoverListener={canUseHostConfirmation && isHostConfirmationObligatory}
              disableFocusListener={canUseHostConfirmation && isHostConfirmationObligatory}>
              <span>
                <Controller
                  control={control}
                  name="hostConfirmationInEmail"
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                      labelPlacement="start"
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          onBlur={field.onBlur}
                          name={field.name}
                          disabled={!canUseHostConfirmation || !isHostConfirmationObligatory}
                        />
                      }
                      label={t('destination:kiosk.hostConfirmationInEmail')}
                    />
                  )}
                />
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={12}>
            <Tooltip
              title={getMessage([
                {
                  check: !isKioskEnabled,
                  message: t('destination:kiosk.deviceConfirmationDisabledNoKiosksSelected'),
                },
                {
                  check: isKioskEnabled && !hasAssignedDevices,
                  message: t('destination:kiosk.deviceConfirmationDisabledNoDevices'),
                },
              ])}
              disableHoverListener={canUseDeviceConfirmation}
              disableFocusListener={canUseDeviceConfirmation}>
              <span>
                <Controller
                  control={control}
                  name="deviceConfirmation"
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                      labelPlacement="start"
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          onBlur={field.onBlur}
                          name={field.name}
                          disabled={!canUseDeviceConfirmation}
                        />
                      }
                      label={t('destination:kiosk.deviceConfirmation')}
                    />
                  )}
                />
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={12}>
            <Tooltip
              title={!isKioskEnabled ? t('destination:kiosk.kioskVisitHoursDisabled') : ''}
              disableHoverListener={isKioskEnabled}
              disableFocusListener={isKioskEnabled}>
              <span>
                <Controller
                  control={control}
                  name="kioskVisitHours"
                  render={({ field, fieldState: { error } }) => (
                    <InputField
                      {...field}
                      fullWidth
                      label={t('destination:kiosk.kioskVisitHours')}
                      required
                      type="number"
                      error={!!error?.message}
                      helperText={error?.message}
                      disabled={!isKioskEnabled}
                    />
                  )}
                />
              </span>
            </Tooltip>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
          }}>
          <Button
            color="primary"
            onClick={() => send({ type: 'BACK' })}
            size="large"
            sx={{ mr: 2 }}
            type="button"
            variant="text">
            {t('common:button.back')}
          </Button>
          <Button
            color="primary"
            size="large"
            type="button"
            variant="contained"
            onClick={() => {
              send({ type: 'NEXT' });
            }}>
            {t('common:button.nextStep')}
          </Button>
        </Box>
      </Box>
    </div>
  );
};
